import type { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { warmImageryCache } from "../../../hooks/use-imagery";
import { MapPreviewImage } from "../map-preview";
import { Section } from "../view/section";
import { AddressAutocomplete } from "./address-autocomplete";
import { FormField, FormInput } from "./form-field";
import type { InsuranceFormData } from "./types";

interface Props {
  register: UseFormRegister<InsuranceFormData>;
  watch: UseFormWatch<InsuranceFormData>;
  setValue: UseFormSetValue<InsuranceFormData>;
}

export function SectionInsuredForm({ register, watch, setValue }: Props): React.ReactElement {
  const address = watch("address");
  const latitude = watch("latitude");
  const longitude = watch("longitude");
  return (
    <Section id="insured" title="Insured & Contact">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <FormField label="Insured" htmlFor="insured" className="col-span-2">
            <FormInput
              id="insured"
              placeholder="Company or individual name"
              {...register("insured")}
              data-testid="field-insured"
            />
          </FormField>
          <FormField label="Contact Full Name" htmlFor="contactFullName">
            <FormInput id="contactFullName" {...register("contactFullName")} />
          </FormField>
          <FormField label="Contact First Name" htmlFor="contactFirstName">
            <FormInput id="contactFirstName" {...register("contactFirstName")} />
          </FormField>
          <FormField label="Contact Address 1" htmlFor="contactAddress1">
            <FormInput id="contactAddress1" {...register("contactAddress1")} />
          </FormField>
          <FormField label="Contact Address 2" htmlFor="contactAddress2">
            <FormInput id="contactAddress2" {...register("contactAddress2")} />
          </FormField>
          <FormField label="Contact Phone" htmlFor="contactPhone">
            <FormInput id="contactPhone" type="tel" {...register("contactPhone")} />
          </FormField>
          <FormField label="Contact Email" htmlFor="contactEmail">
            <FormInput id="contactEmail" type="email" {...register("contactEmail")} />
          </FormField>
        </div>
        <div className="space-y-3">
          <FormField label="Address" htmlFor="address" hint="with lookup">
            <AddressAutocomplete
              id="address"
              value={address}
              onChange={(value) => setValue("address", value, { shouldDirty: true })}
              onSelect={({ address: selected, lat, lng }) => {
                setValue("address", selected, { shouldDirty: true });
                setValue("latitude", lat, { shouldDirty: true });
                setValue("longitude", lng, { shouldDirty: true });
                warmImageryCache(lat, lng);
              }}
              placeholder="Start typing for address lookup..."
              biasLatitude={latitude}
              biasLongitude={longitude}
            />
          </FormField>
          <FormField label="Building Name" htmlFor="buildingName">
            <FormInput id="buildingName" {...register("buildingName")} />
          </FormField>
          <FormField label="Number of Residential Units" htmlFor="numberOfUnits">
            <FormInput
              id="numberOfUnits"
              type="number"
              min={1}
              {...register("numberOfUnits", { valueAsNumber: true })}
            />
          </FormField>
          {latitude != null && longitude != null ? (
            <MapPreviewImage lat={latitude} lng={longitude} className="h-36 mt-2" data-testid="map-preview-form" />
          ) : (
            <div className="h-36 rounded-md border bg-muted flex items-center justify-center text-sm text-muted-foreground mt-2">
              Map preview (will show geocoded location)
            </div>
          )}
        </div>
        <div className="md:col-span-2 grid grid-cols-3 gap-x-3 gap-y-2 pt-2 border-t">
          <p className="col-span-3 text-xs text-muted-foreground">
            Usually the contact; a broker who instructs takes the invoice.
          </p>
          <FormField label="Instructing Party" htmlFor="instructingParty">
            <FormInput id="instructingParty" {...register("instructingParty")} data-testid="field-instructing-party" />
          </FormField>
          <FormField label="Instructing Email" htmlFor="instructingEmail">
            <FormInput
              id="instructingEmail"
              type="email"
              {...register("instructingEmail")}
              data-testid="field-instructing-email"
            />
          </FormField>
          <FormField label="Invoice To" htmlFor="invoiceTo">
            <FormInput id="invoiceTo" {...register("invoiceTo")} data-testid="field-invoice-to" />
          </FormField>
        </div>
      </div>
    </Section>
  );
}
